import { useQuery } from '@tanstack/react-query';
import {
  fetchTeam,
  fetchTeamScoring,
  type BETeamDetail,
  type BETeamScoringDetail,
  type TeamDetailResponse,
  type TeamScoringResponse,
} from '@/lib/network';

type ViewedTeamResponse = TeamDetailResponse | TeamScoringResponse;

export const getViewedTeamQueryKey = (teamId: string, gameweekId?: string) => {
  if (gameweekId) {
    // Include 'with-fixtures' segment because fetchTeamScoring always passes
    // include_gameweek_fixtures=true now and the response shape differs.
    return ['viewed-team', 'scoring', teamId, gameweekId, 'with-fixtures'];
  }
  return ['viewed-team', 'detail', teamId];
};

export interface UseFetchViewedTeamResult {
  data: BETeamDetail | BETeamScoringDetail | null;
  isLoading: boolean;
  isError: boolean;
}

export const useFetchViewedTeam = (
  teamId: string | undefined,
  gameweekId: string | undefined
): UseFetchViewedTeamResult => {
  const detailQuery = useQuery<
    ViewedTeamResponse,
    Error,
    BETeamDetail | BETeamScoringDetail | null
  >({
    queryKey: getViewedTeamQueryKey(teamId ?? '', gameweekId),
    queryFn: async () => {
      if (!teamId) {
        throw new Error('teamId is required to fetch viewed team');
      }
      if (gameweekId) {
        return (await fetchTeamScoring(teamId, gameweekId, true)) as ViewedTeamResponse;
      }
      return (await fetchTeam(teamId)) as ViewedTeamResponse;
    },
    select: response => response.data ?? null,
    enabled: !!teamId,
    staleTime: 0,
    gcTime: 1000 * 60,
  });

  const data = detailQuery.data ?? null;
  const isLoading = detailQuery.isLoading;
  const isError = detailQuery.isError;

  return { data, isLoading, isError };
};
